<template>
    <div
            v-if="options.length > 0"
            class="switcher d-inline"
            @click.stop
    >
        <template v-if="disableCustomCss">
            <b-dropdown
                    :disabled="disabled"
                    toggle-class="default-toggle"
                    @show="clicked"
            >
                <template slot="button-content">
                    <slot name="switcher-toggler">
                        <div class="switcher-content">
                            <i
                                    :class="currentOption ? currentOption.iconClasses : ''"
                                    class="switcher-icon"
                            />
                            <span
                                    class="pl-1"
                                    v-if="currentOption && currentOption.text"
                            >
                                {{ currentOption.text }}
                            </span>
                            <span
                                    class="pl-1"
                                    v-else-if="currentOption"
                            >
                                {{ currentOption.value }}
                            </span>
                            <span
                                    class="pl-1"
                                    v-else-if="placeholder"
                            >
                                {{ placeholder }}
                            </span>
                        </div>
                    </slot>
                </template>

                <slot name="switcher-list">
                    <VueCustomScrollbar
                            :settings="scrollSettings"
                            class="scroll-area"
                    >
                        <div class="text-center" v-if="loading">
                            <b-spinner small label="Loading ... "></b-spinner>
                        </div>
                        <b-dropdown-item
                                v-else
                                v-for="(option, key) in options"
                                :key="`switcher-option-${key}`"
                                :disabled="!!option.disabled"
                                @click="switcherItemClick(option)"
                        >
                            <b-row>
                                <b-col
                                        v-if="option.iconClasses"
                                        cols="2"
                                >
                                    <i :class="option.iconClasses"/>
                                </b-col>
                                <b-col v-if="option.text">
                                    {{ option.text }}
                                </b-col>
                                <b-col v-else>
                                    {{ option.value }}
                                </b-col>
                            </b-row>
                        </b-dropdown-item>
                    </VueCustomScrollbar>
                </slot>
            </b-dropdown>
        </template>
        <template v-else>
            <b-dropdown
                    class="dropdown"
                    toggle-class="switcher-toggler"
                    menu-class="switcher-list"
                    :disabled="disabled"
                    :right="alignRight"
                    @show="clicked"
            >
                <template slot="button-content">
                    <slot name="switcher-toggler">
                        <div class="switcher-content w-75">
                            <template v-if="typeof value === 'undefined' && currentOption">
                                <i
                                        :class="currentOption.iconClasses"
                                        class="switcher-icon"
                                />
                                <span
                                        class="pl-1"
                                        v-if="currentOption.text"
                                >
                                    {{ currentOption.text }}
                                </span>
                                <span
                                        class="pl-1"
                                        v-else
                                >
                                    {{ currentOption.value }}
                                </span>
                            </template>
                            <template v-if="typeof value !== 'undefined'">
                                <span
                                        class="pl-1"
                                >
                                    {{ value }}
                                </span>
                            </template>
                        </div>
                    </slot>
                </template>

                <slot name="switcher-list">
                    <VueCustomScrollbar
                            :settings="scrollSettings"
                            class="scroll-area"
                    >
                        <div class="text-center" v-if="loading">
                            <b-spinner small label="Loading ... "></b-spinner>
                        </div>
                        <b-dropdown-item
                                v-else
                                v-for="(option, key) in options"
                                :key="`switcher-option-${key}`"
                                :disabled="!!option.disabled"
                                @click="switcherItemClick(option)"
                        >

                            <b-row>
                                <b-col
                                        v-if="option.iconClasses"
                                        cols="2"
                                        class="p-0"
                                >
                                    <i :class="option.iconClasses"/>
                                </b-col>
                                <b-col v-if="option.text">
                                    {{ option.text }}
                                </b-col>
                                <b-col v-else>
                                    {{ option.value }}
                                </b-col>
                            </b-row>
                        </b-dropdown-item>
                    </VueCustomScrollbar>
                </slot>
            </b-dropdown>
        </template>
    </div>
</template>

<script>
import './Switcher.scss';
import VueCustomScrollbar from 'vue-custom-scrollbar';

export const FINISH_REQUEST = 'loader';

export default {
    name: 'Switcher',
    components: {VueCustomScrollbar},
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            default: () => [],
        },
        defaultOptionValue: {
            type: [String, Number],
            required: false,
            default: '',
        },
        disableCustomCss: {
            type: Boolean,
            default: false,
        },
        autoSetCurrentOption: {
            type: Boolean,
            default: true,
        },
        value: {
            type: [String, Number],
            default: undefined,
        },
        placeholder: {
            type: String,
        },
        alignRight: {
            type: Boolean,
            default: false,
        },
        dropdownActionInit: {
            type: Function,
            required: false,
        },
    },
    data() {
        return {
            currentOption: this.options.length > 0 && this.autoSetCurrentOption ? this.options[0] : null,
            scrollSettings: {
                wheelPropagation: false,
            },
            loading: false,
        };
    },
    mounted() {
        if (this.defaultOptionValue) {
            this.setCurrentOption(this.defaultOptionValue);
        }
        if (this.currentOption) {
            this.$emit('input', this.currentOption.uuid);
        }
        this.$bus.$on(FINISH_REQUEST, this.requestDone);
    },
    watch: {
        defaultOptionValue(newValue) {
            this.setCurrentOption(newValue);
        },

        options: {
            handler(newValue, oldValue) {
                if (this.value && newValue.length && this.currentOption === null) {
                    this.setCurrentOption(this.value);
                }
            },
        }
    },
    methods: {
        requestDone() {
            this.loading = false;
        },
        clicked() {
            if (typeof this.dropdownActionInit === 'function' && this.dropdownActionInit !== 'undefined') {
                this.loading = true;
                this.dropdownActionInit();
            }
        },
        setCurrentOption(givenOption) {
            const newOption = this.options.find((option) => {
                return option.value === givenOption || option.uuid === givenOption;
            });
            this.currentOption = newOption ? newOption : null;
        },
        switcherItemClick(selectedOption) {
            if (selectedOption !== this.currentOption) {
                this.currentOption = selectedOption;
                this.$emit('switcher:item-click', selectedOption);
                this.$emit('input', selectedOption.uuid);
            }
            this.$emit('switcher:item-selected', selectedOption);
        },
    },
    beforeDestroy() {
        this.$bus.$off(FINISH_REQUEST, this.requestDone);
    }
};
</script>
